import React from 'react';
import type { Theme } from '@material-ui/core/styles/createMuiTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import { makeStyles } from '@material-ui/styles';
import { colors } from '@skyslope/mache';
import VisibilitySensor from 'react-visibility-sensor';
import { MD_MEDIA_QUERY, SKELETON_WIDTH, SKELETON_HEIGHT } from '../../common/constants';
import { boxShadows } from '../../theme';
import type { PdfPageProps } from './PdfPage';

const useStyles = makeStyles<Theme, PdfPageSkeletonProps>(
  (theme) => ({
    pageWrapper: {
      marginBottom: theme.spacing(6),
    },
    pageContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    pageSkeleton: {
      boxShadow: boxShadows.main,
      backgroundColor: 'rgba(255,255,255,0.5)',
      position: 'relative',
      height: 0,
    },
    pageNumber: {
      fontSize: '14px',
      color: colors.grey[800],
      paddingLeft: theme.spacing(),
      paddingBottom: theme.spacing(),
    },
    pageFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '8px',
      color: colors.grey[800],
    },
    footerDocument: {
      whiteSpace: 'nowrap',
      width: '65%',
      textOverflow: 'ellipsis',
      overflow: 'auto',
    },
    visibilityPixel: {
      position: 'absolute',
      left: '50%',
      top: 200,
      zIndex: 0,
      width: 1,
      height: 1,
    },
  }),
  { name: 'PdfPageSkeleton' }
);

type PdfPageSkeletonProps = {
  children: React.ReactNode;
  /** 1-based index of this page relative to all other pages in all other documents */
  crossDocPageIndex: number;
  onVisible: (crossDocPageIndex: number) => void;
  totalPages: number;
  totalDocuments: number;
  documentName: string;
  documentPages: number;
} & Pick<PdfPageProps, 'zoom' | 'pageDimensions' | 'documentIndex' | 'pageNumber'>;

export const PdfPageSkeleton = React.memo(function PdfPageSkeleton(props: PdfPageSkeletonProps) {
  // The reason we use isMobile is on phones in portrait mode, the media query won't trigger. This
  // also prevents being fully zoomed out while on small desktop windows.
  const {
    children,
    zoom,
    pageDimensions,
    crossDocPageIndex,
    onVisible,
    documentPages,
    documentName,
    documentIndex,
    pageNumber,
    totalDocuments,
    totalPages,
  } = props;

  const isMobileWidth = useMediaQuery(MD_MEDIA_QUERY);
  const classes = useStyles(props);

  const selector = crossDocPageIndex.toString();

  const pageWidth = pageDimensions?.width || SKELETON_WIDTH;
  const width = isMobileWidth ? '100%' : pageWidth * zoom;
  const aspectRatio = pageDimensions?.height
    ? (pageDimensions?.height / pageWidth) * 100
    : (SKELETON_HEIGHT / SKELETON_WIDTH) * 100;

  return (
    <div className={classes.pageWrapper} id={`page-${crossDocPageIndex}`} style={{ width }}>
      {isMobileWidth ? null : <div className={classes.pageNumber}>{`Page ${pageNumber + 1}/${documentPages}`}</div>}

      <div className={classes.pageSkeleton} style={{ width, paddingTop: `${aspectRatio}%` }}>
        <div className={classes.pageContainer} id={`p-${selector}`}>
          <VisibilitySensor
            onChange={(isVisible: boolean) => {
              if (isVisible) {
                onVisible(crossDocPageIndex);
              }
            }}
          >
            <div className={classes.visibilityPixel} />
          </VisibilitySensor>

          {children}
        </div>
      </div>

      {isMobileWidth ? (
        <div className={classes.pageFooter}>
          <div className={classes.footerDocument}>{`Doc ${documentIndex + 1}/${totalDocuments}: ${documentName}`}</div>
          <div>{`Page ${pageNumber + 1}/${totalPages}`}</div>
        </div>
      ) : null}
    </div>
  );
});
